<script setup lang="ts">
import { ref, computed } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'
import TermsCheckbox from '@uiKit/components/TermsCheckbox.vue'

import InvestPropertyStrip from './InvestPropertyStrip.vue'
import TokenStepper from './TokenStepper.vue'
import InvestmentSummaryCard from './InvestmentSummaryCard.vue'
import PaymentMethodSelector, { type PaymentMethod } from './PaymentMethodSelector.vue'

import { formatAed } from '@shared/utils'

// PaymentMethod is informational-only — the backend resolves the actual
// split (walletAmount / pgAmount) at order creation based on wallet
// balance + offering rules. The selector currently exposes wallet only.
export interface PlaceOrderPayload {
  tokens: number
  totalPayable: number
  paymentMethod: PaymentMethod
}

export interface FeeBreakdown {
  dldFee: number
  acquisitionFee: number
  tokenisationFee: number
  platformFee: number
}

export interface Calculator {
  tokenPrice: number
  minTokens: number
  maxTokens: number
  tokenAvailable: number
  // Server-calculated amounts driven by current tokenCount
  grossAmount: number
  netAmount: number
  platformFeeAmount: number
  dldFeeAmount: number
  acquisitionFeeAmount: number
  tokenisationFeeAmount: number
  feesBreakdown?: FeeBreakdown
  isFetching: boolean
}

interface Props {
  propertyImage: string
  propertyTitle: string
  propertyLocation: string
  calculator: Calculator
  walletBalance: number
  submitting: boolean
  errorMessage: string | null
  feesError: string | null
  isInsufficientBalance: boolean
  tokenCount: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:tokenCount', value: number): void
  (e: 'place-order', payload: PlaceOrderPayload): void
  (e: 'open-terms'): void
  (e: 'add-funds'): void
}>()

const paymentMethod = ref<PaymentMethod>('wallet')
const termsAccepted = ref(false)

const totalFeeAmount = computed(
  () =>
    props.calculator.platformFeeAmount +
    props.calculator.dldFeeAmount +
    props.calculator.acquisitionFeeAmount +
    props.calculator.tokenisationFeeAmount,
)

const exceedsTokens = computed(() => props.tokenCount > props.calculator.tokenAvailable)
const exceedsBalance = computed(
  () => paymentMethod.value === 'wallet' && props.calculator.netAmount > props.walletBalance,
)
const isGatewaySelected = computed(() => paymentMethod.value === 'gateway')

const canPlace = computed(
  () =>
    termsAccepted.value &&
    !isGatewaySelected.value &&
    !exceedsTokens.value &&
    !exceedsBalance.value &&
    !props.feesError &&
    props.tokenCount > 0 &&
    !props.calculator.isFetching,
)

const onPlace = () => {
  if (!canPlace.value) return
  emit('place-order', {
    tokens: props.tokenCount,
    totalPayable: props.calculator.netAmount,
    paymentMethod: paymentMethod.value,
  })
}
</script>

<template>
  <Box direction="col" gap="4" class="invest-form__body">
    <InvestPropertyStrip
      :image="propertyImage"
      :title="propertyTitle"
      :location="propertyLocation"
    />

    <TokenStepper
      :model-value="tokenCount"
      :min-tokens="calculator.minTokens"
      :tokens-available="calculator.tokenAvailable"
      :token-price="calculator.tokenPrice"
      :investment-value="calculator.grossAmount"
      @update:model-value="(v) => emit('update:tokenCount', v)"
    />

    <!-- Fees calc error for the current quantity (e.g. exceeds available
         tokens / max allocation). Shown inline so the user can adjust the
         token count above and retry — never replaces the form. -->
    <Message v-if="feesError" color="danger" variant="simple" size="small" :message="feesError" />

    <InvestmentSummaryCard
      :token-price="calculator.tokenPrice"
      :gross-amount="calculator.grossAmount"
      :total-fee-amount="totalFeeAmount"
      :net-amount="calculator.netAmount"
      :platform-fee-amount="calculator.platformFeeAmount"
      :dld-fee-amount="calculator.dldFeeAmount"
      :acquisition-fee-amount="calculator.acquisitionFeeAmount"
      :tokenisation-fee-amount="calculator.tokenisationFeeAmount"
      :fees-breakdown="calculator.feesBreakdown"
      :exceeds-tokens="exceedsTokens"
      :tokens-available="calculator.tokenAvailable"
      :is-fetching="calculator.isFetching"
    />

    <PaymentMethodSelector
      v-model="paymentMethod"
      :wallet-balance="walletBalance"
      :exceeds-balance="exceedsBalance"
    />

    <TermsCheckbox v-model="termsAccepted">
      I have read and agree to the
      <RouterLink to="#" class="invest-form__terms-link" @click.prevent="emit('open-terms')">
        Investment Terms &amp; Conditions
      </RouterLink>
      and understand the risks involved.
    </TermsCheckbox>

    <Box v-if="errorMessage" direction="col" gap="2">
      <Message color="danger" variant="simple" size="small" :message="errorMessage" />
      <Button
        v-if="isInsufficientBalance"
        label="Add funds"
        icon="plus"
        variant="outline"
        size="small"
        block
        @click="emit('add-funds')"
      />
    </Box>
  </Box>

  <Box class="invest-form__footer">
    <Button
      :label="`Place order · ${formatAed(calculator.netAmount)}`"
      icon="arrow-right"
      icon-pos="right"
      size="large"
      block
      :loading="submitting"
      :disabled="!canPlace || submitting"
      @click="onPlace"
    />
  </Box>
</template>

<style scoped>
.invest-form__body {
  flex: 1;
  padding: 1.25rem 1.5rem 1rem;
}

.invest-form__terms-link {
  color: var(--p-primary-600);
  text-decoration: underline;
  cursor: pointer;
}

.invest-form__footer {
  position: sticky;
  bottom: 0;
  padding: 0.75rem 1.5rem 1rem;
  background: var(--p-surface-0);
  border-top: 1px solid var(--p-content-border-color);
  box-shadow: 0 -8px 16px -16px rgba(0, 0, 0, 0.1);
}
</style>
